/**
 * bbox coordinate helpers — single source of truth for the [y0,x0,y1,x1] (0..1000, Gemini)
 * convention used across the @tebiki/shared contract and the percentage-based overlay the
 * `Frame` component draws (top/left/width/height as % of the image element).
 */
import type { BBox as SharedBBox } from "@tebiki/shared";
import type { BBox as FrameBBox } from "../components/Frame.js";

/** Convert a shared [y0,x0,y1,x1] (0..1000) bbox into a Frame overlay (percentages 0..100). */
export function bboxToFrame(bbox: SharedBBox | null | undefined, label?: string | null): FrameBBox | null {
  if (!bbox) return null;
  const [y0, x0, y1, x1] = bbox;
  return {
    top: (y0 / 1000) * 100,
    left: (x0 / 1000) * 100,
    width: ((x1 - x0) / 1000) * 100,
    height: ((y1 - y0) / 1000) * 100,
    label: label ?? undefined,
  };
}

/** Convert percentage rect (0..100) back into a shared [y0,x0,y1,x1] (0..1000), clamped + ordered. */
export function frameToBbox(rect: { top: number; left: number; width: number; height: number }): SharedBBox {
  const clamp = (n: number) => Math.max(0, Math.min(1000, Math.round(n)));
  const y0 = clamp((rect.top / 100) * 1000);
  const x0 = clamp((rect.left / 100) * 1000);
  const y1 = clamp(((rect.top + rect.height) / 100) * 1000);
  const x1 = clamp(((rect.left + rect.width) / 100) * 1000);
  // Guarantee y0<=y1, x0<=x1 so a flipped drag never produces a negative-size box.
  return [Math.min(y0, y1), Math.min(x0, x1), Math.max(y0, y1), Math.max(x0, x1)];
}
